/* eslint-disable prefer-template */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import {
  StatementSummary,
  Workspace,
  sampleWorkspaces,
  summarize,
  workspaceFromStatement,
} from './model';

export const API = 'posting';
export const name = 'postingSlice';

const seed = sampleWorkspaces();

interface InitState {
  isLoading: boolean,
  isSaving: boolean,
  items: StatementSummary[],
  workspaces: Record<string, Workspace>,
}

function NewReducer() {
  const initialState: InitState = {
    isLoading: false,
    isSaving: false,
    items: seed.map(summarize),
    workspaces: Object.fromEntries(seed.map((workspace) => [workspace.id, workspace])),
  };

  const reducers = {
    resetSlice: () => initialState,
    startRead: (state: InitState) => {
      state.isLoading = true;
    },
    finishRead: (state: InitState, action: PayloadAction<Workspace[] | null>) => {
      state.isLoading = false;

      if (!action.payload) {
        return;
      }

      state.workspaces = Object.fromEntries(action.payload.map((workspace) => [workspace.id, workspace]));
      state.items = action.payload.map(summarize);
    },
    startSave: (state: InitState) => {
      state.isSaving = true;
    },
    failSave: (state: InitState) => {
      state.isSaving = false;
    },
    saveWorkspace: (state: InitState, action: PayloadAction<Workspace>) => {
      const workspace = action.payload;

      state.workspaces[workspace.id] = workspace;
      state.items = state.items.map((item) => (item.id === workspace.id ? summarize(workspace) : item));
      state.isSaving = false;
    },
    ensureFromStatement: (state: InitState, action: PayloadAction<any>) => {
      const row = action.payload || {};
      const id = String(row.id ?? '');
      const number = String(row.statement_number ?? '');
      const existing = (id && state.workspaces[id])
        || Object.values(state.workspaces).find((workspace) => workspace.statementNumber === number);

      if (existing || !id) {
        return;
      }

      const workspace = workspaceFromStatement(row);

      state.workspaces[workspace.id] = workspace;
      state.items.push(summarize(workspace));
    },
    removeWorkspace: (state: InitState, action: PayloadAction<string>) => {
      const id = action.payload;

      delete state.workspaces[id];
      state.items = state.items.filter((item) => item.id !== id);
    },
  };

  const slice = createSlice({
    name,
    initialState,
    reducers,
  });

  const { actions } = slice;

  const readWorkspaces = (payload: any): Workspace[] | null => {
    const rows = Array.isArray(payload) ? payload : payload?.data;

    if (!Array.isArray(rows) || !rows.length) {
      return null;
    }

    const usable = rows.filter((row) => row && Array.isArray(row.items) && Array.isArray(row.links) && row.statementNumber);

    return usable.length ? usable : null;
  };

  const apis = {
    callReadApi: () => async (dispatch: any) => {
      dispatch(actions.startRead());

      await axios.get(API).then((result: any) => {
        dispatch(actions.finishRead(readWorkspaces(result.data?.data)));
      }).catch(() => {
        dispatch(actions.finishRead(null));
      });
    },

    callSaveApi: (workspace: Workspace) => async (dispatch: any) => {
      dispatch(actions.startSave());

      try {
        await axios.put(API + '/' + workspace.id, workspace);
        dispatch(actions.saveWorkspace(workspace));
        return true;
      } catch {
        dispatch(actions.failSave());
        return false;
      }
    },
  };

  return {
    reducer: slice.reducer,
    ...actions,
    ...apis,
  };
}

export default NewReducer();
